import { IRequestContext } from '@msdyn365-commerce/core-internal';
import { ITelemetry } from '@msdyn365-commerce/telemetry-internal';
import { FilterBuilder, Settings } from '@relewise/client';
import { getTranslation, TranslationKey } from '../../../../shared/functions/get-translation';
import { RELEWISE_MINIMUM_STOCK_COUNT } from '../../const';
import { DisplayedAtLocation } from '../../types/mapped';
import { RelewiseUserService } from '../UserService';

export interface InitializedRelewiseService {
    disabled: false;
    telemetry: ITelemetry;
    currency: string;
    currencyId: string;
    locale: string;
    legalEntityId: string;
    environmentName: string;
    relewiseDatasetId: string;
    relewiseApiKey: string;
    relewiseUrl: string;
    relewiseUserService: RelewiseUserService;
}

export abstract class BaseRelewiseService<TInitializedService extends InitializedRelewiseService> {
    private readonly request: IRequestContext;

    protected telemetry: ITelemetry;
    protected currency: string | undefined;
    protected currencyId: string | undefined;
    protected locale: string | undefined;
    protected legalEntityId: string | undefined;
    protected environmentName: string | undefined;
    protected relewiseDatasetId: string;
    protected relewiseApiKey: string;
    protected relewiseUrl: string;
    protected sitePath: string | undefined;
    protected baseUrl: string | undefined;

    protected serviceName: string;
    protected isDev = process.env.NODE_ENV === 'development';
    protected showTrackingLogs = false;
    protected disabled = true;

    protected relewiseUserService: RelewiseUserService | undefined;

    constructor(request: IRequestContext, telemetry: ITelemetry, serviceName: string) {
        this.request = request;
        this.telemetry = telemetry;
        this.serviceName = serviceName;

        const { relewiseApiKey, relewiseDatasetId, relewiseUrl, environmentName } = request.app.config ?? {};
        this.environmentName = environmentName;
        this.relewiseDatasetId = relewiseDatasetId;
        this.relewiseApiKey = relewiseApiKey;
        this.relewiseUrl = relewiseUrl;
        if (!relewiseApiKey || !relewiseDatasetId || !relewiseUrl || !this.environmentName) {
            this.logError(
                'Service could not be initialized: Missing Relewise configuration. Expected relewiseApiKey, relewiseDatasetId, relewiseUrl, and environmentName. Add this config in the CMS.'
            );
            return;
        }

        this.legalEntityId = request.channel?.InventLocationDataAreaId;
        if (!this.legalEntityId) {
            this.logError('Service could not be initialized: Legal Entity ID missing.');
            return;
        }

        this.locale = request.locale;
        if (!this.locale) {
            this.logError('Service could not be initialized: Locale missing.');
            return;
        }

        this.currency = request.channel?.Currency;
        if (!this.currency) {
            this.logError('Service could not be initialized: Currency missing.');
            return;
        }
        this.currencyId = `${this.currency}_${this.legalEntityId}`;

        this.sitePath = request.sitePath;
        if (!this.sitePath) {
            this.logError('Service could not be initialized: Site path is not configured.');
            return;
        }

        this.baseUrl = request.url.requestUrl.origin;
        if (!this.baseUrl) {
            this.logError('Service could not be initialized: Base URL is not configured.');
            return;
        }

        this.showTrackingLogs = request.app.config?.showTrackingLogs || this.isDev;

        this.relewiseUserService = new RelewiseUserService(request);

        this.disabled = false;
    }

    protected logError(message: string) {
        const fullMessage = `[Relewise ${this.serviceName}] ${message}`;
        console.error(fullMessage);
        this.telemetry.error(fullMessage);
    }

    protected isEnabled(): this is TInitializedService {
        return !this.disabled;
    }

    protected translateCmsString(key: TranslationKey, fallback?: string): string | undefined {
        return getTranslation(key, this.request) ?? fallback;
    }

    protected async getApiRequestSettings(displayedAtLocation: DisplayedAtLocation): Promise<Settings> {
        if (!this.isEnabled()) {
            throw new Error('Service is not enabled. Cannot get API request settings.');
        }

        const user = await this.relewiseUserService.getUser();
        return {
            language: this.locale,
            currency: this.currencyId,
            displayedAtLocation,
            user
        };
    }

    protected getSelectedProductPropertiesForSimpleProduct() {
        return {
            displayName: true,
            dataKeys: [`Colors_${this.legalEntityId}`, 'RecordId'],
            brand: true
        };
    }

    protected getSelectedVariantPropertiesForSimpleProduct() {
        return {
            displayName: true,
            pricing: true,
            dataKeys: [
                'RecordId',
                'PrimaryImageUrl',
                'RelativeUrl',
                'Color',
                'ColorHexCode',
                'ColorRefinerGroup',
                `PrimarySizeVariant_${this.legalEntityId}`,
                `ProductLabels_${this.legalEntityId}`,
                `MemberPrice_${this.legalEntityId}`
            ]
        };
    }

    protected addProductEnvironmentFilter(filterBuilder: FilterBuilder) {
        filterBuilder.addProductDataFilter(
            'Environment',
            b => b.addEqualsCondition({ value: this.environmentName, type: 'String', isCollection: false }),
            true,
            true
        );
    }

    protected addCategoryEnvironmentFilter(filterBuilder: FilterBuilder) {
        filterBuilder.addProductCategoryDataFilter(
            'Environment',
            b => b.addEqualsCondition({ value: this.environmentName, type: 'String', isCollection: false }),
            true,
            true
        );
    }

    protected addProductAndVariantStockFilters(filterBuilder: FilterBuilder) {
        filterBuilder.addProductDataFilter(
            `HasVariantInStock_${this.legalEntityId}`,
            b => b.addEqualsCondition({ value: true, type: 'Boolean', isCollection: false }),
            true,
            true
        );
        filterBuilder.addVariantDataFilter(
            `StockCount_${this.legalEntityId}`,
            b => b.addGreaterThanCondition(RELEWISE_MINIMUM_STOCK_COUNT),
            true,
            true
        );
        filterBuilder.addVariantDataFilter(
            `InAssortment_${this.legalEntityId}`,
            b => b.addEqualsCondition({ value: true, type: 'Boolean', isCollection: false }),
            true,
            true
        );
    }
}
